import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { ComponentType } from "@/utils/types/componentTypes";
import { fetchComponentTypes, createComponentType, updateComponentType, deleteComponentType } from "@/utils/services/componentTypeService";
import { ComponentTypesTable } from "@/components/component-types/ComponentTypesTable";
import { ComponentTypeDialog } from "@/components/component-types/ComponentTypeDialog";
import { DeleteComponentTypeDialog } from "@/components/component-types/DeleteComponentTypeDialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";

const ComponentTypes = () => {
  const [sortColumn, setSortColumn] = useState<string>("group_name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedComponentType, setSelectedComponentType] = useState<ComponentType | null>(null);

  const queryClient = useQueryClient();
  const { subscriptionData } = useAuth();

  // Query for fetching component types with subscription filter
  const { data: componentTypes = [], isLoading } = useQuery({
    queryKey: ["componentTypes", subscriptionData.subscription_id],
    queryFn: () => fetchComponentTypes(subscriptionData.subscription_id),
    enabled: !!subscriptionData.subscription_id,
  });

  // Recarregar quando a subscrição mudar
  useEffect(() => {
    if (subscriptionData.subscription_id) {
      queryClient.invalidateQueries({ queryKey: ["componentTypes", subscriptionData.subscription_id] });
    }
  }, [subscriptionData.subscription_id, queryClient]);

  // Mutation for creating a component type
  const createMutation = useMutation({
    mutationFn: ({ name, icon, groupName }: { name: string; icon: string | null; groupName: string | null }) => 
      createComponentType(name, icon, groupName, subscriptionData.subscription_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["componentTypes", subscriptionData.subscription_id] });
      setIsCreateDialogOpen(false);
    },
  });

  // Mutation for updating a component type
  const updateMutation = useMutation({
    mutationFn: ({ id, name, icon, groupName }: { id: string; name: string; icon: string | null; groupName: string | null }) => 
      updateComponentType(id, name, icon, groupName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["componentTypes", subscriptionData.subscription_id] });
      setIsEditDialogOpen(false);
    },
  });

  // Mutation for deleting a component type
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteComponentType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["componentTypes", subscriptionData.subscription_id] });
      setIsDeleteDialogOpen(false);
    },
  });

  // Sort the component types
  const sortedComponentTypes = [...componentTypes].sort((a, b) => {
    // Primary sort by group_name
    const aGroup = a.group_name || "";
    const bGroup = b.group_name || "";
    
    if (aGroup !== bGroup) {
      return aGroup.localeCompare(bGroup);
    }
    
    // Secondary sort by name
    return a.name.localeCompare(b.name);
  }).sort((a, b) => {
    // Apply user-selected sorting if different from default
    if (sortColumn !== "group_name") {
      const aValue = a[sortColumn as keyof ComponentType];
      const bValue = b[sortColumn as keyof ComponentType];

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
    } else {
      // For group_name sorting, respect the sort direction
      const aGroup = a.group_name || "";
      const bGroup = b.group_name || "";
      
      if (aGroup !== bGroup) {
        return sortDirection === "asc"
          ? aGroup.localeCompare(bGroup)
          : bGroup.localeCompare(aGroup);
      }
      
      // Secondary sort by name (always ascending when sorting by group)
      return sortDirection === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }

    return 0;
  });

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleCreateComponentType = (name: string, icon: string | null, groupName: string | null) => {
    createMutation.mutate({ name, icon, groupName });
  };

  const handleUpdateComponentType = (name: string, icon: string | null, groupName: string | null) => {
    if (selectedComponentType) {
      updateMutation.mutate({ id: selectedComponentType.id, name, icon, groupName });
    }
  };

  const handleDeleteComponentType = () => {
    if (selectedComponentType) {
      deleteMutation.mutate(selectedComponentType.id);
    }
  };

  const handleEditClick = (componentType: ComponentType) => {
    setSelectedComponentType(componentType);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (componentType: ComponentType) => {
    setSelectedComponentType(componentType);
    setIsDeleteDialogOpen(true);
  };

  const handleAddClick = () => {
    setIsCreateDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Tipos de Componentes</h1>
          <p className="text-muted-foreground">
            Gerencie os tipos de componentes usados na sua organização.
          </p>
        </div>
        <Button 
          onClick={handleAddClick}
          className="bg-primary hover:bg-primary/90"
        >
          <Plus className="mr-2 h-4 w-4" /> Novo Tipo
        </Button>
      </div>
      
      <Card className="border border-border/40 shadow-sm">
        <CardContent className="p-0">
          <ComponentTypesTable
            componentTypes={sortedComponentTypes}
            loading={isLoading}
            onSort={handleSort}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onAdd={handleAddClick}
          />
        </CardContent>
      </Card>

      <ComponentTypeDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSave={handleCreateComponentType}
        componentType={null}
        isLoading={createMutation.isPending}
        mode="create"
      />

      <ComponentTypeDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSave={handleUpdateComponentType}
        componentType={selectedComponentType}
        isLoading={updateMutation.isPending}
        mode="edit"
      />

      <DeleteComponentTypeDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        componentType={selectedComponentType}
        onConfirm={handleDeleteComponentType}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default ComponentTypes;
